/** Shared date helpers — display and store convention is MM/DD/YYYY. */

export function parseDateValue(val) {
  if (val === null || val === undefined || val === '') return null;
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;

  if (typeof val === 'object' && !Array.isArray(val)) {
    const nested = val.date ?? val.meeting_date ?? val.value ?? val.iep_date;
    if (nested != null && nested !== val) return parseDateValue(nested);
    return null;
  }

  if (typeof val !== 'string') return null;
  const s = val.trim();
  if (!s) return null;

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const mmdd = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mmdd) {
    const d = new Date(+mmdd[3], +mmdd[1] - 1, +mmdd[2]);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Calendar date for display. ISO YYYY-MM-DD is local, never UTC midnight. */
export function formatDisplayDate(val, fallback = '—') {
  const d = parseDateValue(val);
  if (!d) return fallback;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatMMDDYYYY(val) {
  const d = parseDateValue(val);
  if (!d) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
}

export function formatISODate(val) {
  const d = parseDateValue(val);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isValidMMDDYYYY(str) {
  if (!str || typeof str !== 'string') return false;
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const month = +m[1];
  const day = +m[2];
  const year = +m[3];
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000) return false;
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

/** Auto-insert slashes while typing digits for MM/DD/YYYY. */
export function maskDateInput(raw) {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function emitDateValue(dateOrStr, outputFormat = 'MM/DD/YYYY') {
  if (!dateOrStr) return '';
  if (outputFormat === 'YYYY-MM-DD') return formatISODate(dateOrStr);
  return formatMMDDYYYY(dateOrStr);
}

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Years available in the calendar year dropdown (old DOBs → future due dates). */
export function getYearOptions(centerYear = new Date().getFullYear(), past = 120, future = 30) {
  const start = centerYear - past;
  const end = centerYear + future;
  const years = [];
  for (let y = end; y >= start; y -= 1) years.push(y);
  return years;
}

/** How many years are visible in the year dropdown before scrolling. */
export const VISIBLE_YEAR_COUNT = 12;

/** Approx height (px) of one year row in the custom dropdown. */
export const YEAR_OPTION_HEIGHT_PX = 28;

export function getCalendarCells(year, month) {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  return cells;
}
